import { z } from 'zod';
import { BOOKING_HORIZON_MAX } from '../calendar/constants.js';
import { findCustomAvailabilityIssues } from './custom-availability.js';
import {
  VIRTUAL_LINK_MAX_LENGTH,
  VIRTUAL_PASSCODE_MAX_LENGTH,
  VIRTUAL_PLATFORMS,
} from '../location/virtual-platform.js';
import {
  isDepositAmountValid,
  PUBLISH_BLOCKER_CODES,
  PUBLISH_READINESS_SECTION_KEYS,
} from './publish-readiness.js';

// ============================================================================
// Phase 5 Domain 3: Service Admin Schemas
// ============================================================================

/**
 * Service lifecycle status enum schema.
 * - DRAFT: Editable, not visible to clients
 * - LIVE: Visible and bookable
 * - ARCHIVED: Hidden from new bookings, history preserved
 */
export const serviceStatusSchema = z.enum(['DRAFT', 'LIVE', 'ARCHIVED']);

/**
 * Attendance status enum schema for completed sessions/events.
 * Marked by the admin on the session or event page.
 */
export const attendanceStatusSchema = z.enum(['ATTENDED', 'NO_SHOW']);

// ============================================================================
// Authoring ceilings (create-flow dropped-gates sweep, 2026-08-06)
// ============================================================================
//
// The retired 6-step wizard bounded these fields with stepper controls plus
// matching schema rules; the rebuilt create path kept neither, so a mistyped
// digit persisted a 480-minute buffer or a 99999-minute session that silently
// emptied the bookable calendar. The ceilings are restored HERE, on the shared
// write schemas, so both doors and the editors read one set of numbers.
//
// Exported because the section editors clamp their inputs to the same values —
// a clamp that disagrees with the door is how a late, unattributed 400 happens.

/** Session length ceiling for a session-type offering (99h59m — the old steppers' top). */
export const MAX_SERVICE_DURATION_MINUTES = 5999;

/**
 * Session length ceiling for an event template: 30 days, the event day-count
 * ceiling times 1440. A continuous multi-day event stores its whole run as
 * `duration`, so this must clear `eventDaysCount` at its maximum.
 */
export const MAX_EVENT_DURATION_MINUTES = 43200;

/** Buffer ceiling for a session offering — 2h either side. */
/** Session buffer ceiling: four hours, matching the hand-set event ceiling (Gabriel ruling 2026-09-01; was 120). */
export const MAX_SERVICE_BUFFER_MINUTES = 240;

/**
 * Buffer ceiling for an event offering — 26 hours, not 4.
 *
 * Edge-day blocking (a multi-day placement claiming the rest of its start or
 * end day) writes literal minutes to the local day edge into these same buffer
 * fields, so the ceiling must clear the LONGEST local day ANY timezone the
 * availability picker offers can produce — not the longest one in the US.
 *
 * A one-hour fall-back day runs 25 hours (1,500 minutes), which is where this
 * ceiling first landed. But `availability-timezone-registry.ts` offers the whole
 * IANA set, and Antarctica/Troll shifts by TWO hours: 2026-10-25 is a 26-hour
 * local day, and a start-of-day edge fill on it reaches 1,560 minutes. At 1,500
 * that switch would have written a buffer the write schema rejects.
 *
 * `availability-timezone-registry.buffer-ceiling.test.ts` probes every zone in
 * the registry over a multi-year window and fails if any local day outruns this
 * number, so a future registry addition cannot reopen the gap in silence.
 *
 * Values above 240 normally arise only from edge-day blocking; see
 * `docs/data-model/dm-calendar-availability.md`.
 */
export const MAX_EVENT_BUFFER_MINUTES = 1560;

/**
 * The most an admin can dial in BY HAND for an event buffer, on either side:
 * four hours, in quarter-hour steps (Gabriel ruling 2026-09-01). The buffer
 * steppers on the placement editor and the template's Booking rules both stop
 * here. Anything larger is written by edge-day blocking, never typed — which
 * is why the schema ceiling above stays where it is.
 */
export const MAX_MANUAL_EVENT_BUFFER_MINUTES = 240;

/** Free-cancellation cutoff ceiling (7 days). */
export const MAX_CANCELLATION_CUTOFF_MINUTES = 10080;

const MINUTES_PER_DAY = 1440;

/**
 * How far ahead of an offering a booking may be made at all — the same ceiling
 * the calendar's booking horizon enforces, restated on the write door so the
 * editor's "up to N days" sub-line is a promise the door actually keeps.
 */
export const MAX_ADVANCE_BOOKING_DAYS = BOOKING_HORIZON_MAX;

/**
 * Registration-cutoff ceiling, derived rather than invented: a cutoff further
 * out than the booking horizon closes registration before it could ever open,
 * so one full horizon is the largest cutoff that still describes a bookable
 * event.
 */
export const MAX_BOOKING_CUTOFF_MINUTES = MAX_ADVANCE_BOOKING_DAYS * MINUTES_PER_DAY;

/** How far ahead a reminder may be scheduled (30 days). */
export const MAX_REMINDER_MINUTES_BEFORE = 43200;

/**
 * How far after a session a follow-up may be scheduled (30 days) — the mirror of
 * the reminder ceiling, and the bound the Notifications editor's Later check-in
 * field already clamped to client-side (30 days) while Aftercare had no ceiling
 * on either layer.
 */
export const MAX_FOLLOW_UP_MINUTES_AFTER = 43200;

/** How many duration choices a client picker may offer. */
export const MIN_DURATION_OPTIONS = 2;
export const MAX_DURATION_OPTIONS = 5;

/** Attendee ceiling — one shared number for session capacity and event seats. */
export const MAX_CONCURRENT_BOOKINGS = 999;

/** Invite ceiling per booking (post-MVP feature; the clamp survives). */
export const MAX_INVITES = 99;

/** Form-reuse window ceiling (1 year). */
export const MAX_REUSE_WINDOW_DAYS = 365;

/**
 * Offering names must fit the two-line 32px AdminDetailHero title on a 375px
 * phone. Cross-device measurement found 36 characters is the reliable cap.
 */
export const MAX_NAME_LENGTH = 36;
export const MAX_DESCRIPTION_LENGTH = 500;

/**
 * The one-line catalog blurb ceiling, for `Service.summary` AND for the
 * per-occurrence `CalendarEvent.summary` (2026-08-26).
 *
 * The number the offering door has always enforced, named so the two doors
 * cannot drift: an instance bound looser than the template bound would accept
 * copy the blueprint refuses, and a tighter one would refuse copy the occurrence
 * was SEEDED with.
 */
export const MAX_SUMMARY_LENGTH = 90;

/**
 * Ceiling on a stored deposit amount, in dollars (`validation-wave` 2026-08-26).
 *
 * Deliberately far above any plausible booking so it can never refuse a real
 * one — its whole job is to stop a typo or a bad caller persisting an absurd
 * figure into a money column that had NO upper bound at all. Price stays
 * unbounded above: a deposit is a part-payment, so the meaningful ceiling on it
 * is the price, which the editors enforce as a save blocker.
 */
export const MAX_DEPOSIT_AMOUNT = 1_000_000;

/**
 * The custom heading above the preparation notes — a card title on the client
 * booking detail, where the default ("How to prepare") is 14 characters.
 *
 * Measured on the RAW string, before the service layer strips the label's tags,
 * so markup an admin cannot see counts against the cap. That is survivable at 60
 * and would not be at 40: the editor writes plain text, and the only tag-bearing
 * values come from duplicating a wizard-era offering.
 */
export const MAX_PREPARATION_LABEL_LENGTH = 60;
export const MAX_DURATION_OPTION_LABEL_LENGTH = 50;
export const MAX_DURATION_OPTION_DESCRIPTION_LENGTH = 200;

/**
 * Duration option schema for services with multiple duration/price choices.
 * When a service has hasDurationOptions=true, clients select from these options
 * before choosing a calendar slot.
 *
 * Note: IDs use plain z.string() for consistency with calendar/schemas.ts convention.
 */
export const serviceDurationOptionSchema = z.object({
  id: z.string(),
  serviceId: z.string(),
  durationMinutes: z.number().int().positive(),
  price: z.number().nonnegative(),
  /** Optional display name for this option (e.g., "Coloring Add-On") */
  label: z.string().nullable().optional().default(null),
  /** Optional description of what this option includes */
  description: z.string().nullable().optional().default(null),
  sortOrder: z.number().int().default(0),
});

/**
 * Base service summary schema - matches ServiceSummaryDto from calendar/types.ts
 * Used for composition with admin-specific fields.
 */
const serviceSummaryBaseSchema = z.object({
  id: z.string(),
  name: z.string(),
  duration: z.number().int().positive(),
  minimumNoticeMinutes: z.number().int().nullable(),
  maxConcurrentBookings: z.number().int().min(1),
  isActive: z.boolean(),
  isSharedEvent: z.boolean().nullable().optional(),
  advanceBookingDays: z.number().int().nullable().optional(),
});

/**
 * Extended service schema with admin-specific fields.
 * Composes ServiceSummaryDto with lifecycle and duration option fields.
 *
 * Used for admin dashboard service management (create, edit, archive).
 *
 * Includes superRefine to fail fast before DB constraints:
 * - isActive must equal (status === 'LIVE')
 * - ARCHIVED status requires archivedAt to be set
 */
export const serviceAdminSchema = serviceSummaryBaseSchema
  .extend({
    // Lifecycle fields
    status: serviceStatusSchema,
    archivedAt: z.date().nullable(),

    // Duration options
    hasDurationOptions: z.boolean(),
    durationOptions: z.array(serviceDurationOptionSchema),

    // Additional service details
    description: z.string().nullable(),
    price: z.number().nullable(), // Decimal converted to number
  })
  .superRefine((data, ctx) => {
    // Enforce isActive === (status === 'LIVE') - mirrors DB CHECK constraint
    const expectedIsActive = data.status === 'LIVE';
    if (data.isActive !== expectedIsActive) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `isActive must be ${expectedIsActive} when status is ${data.status}`,
        path: ['isActive'],
      });
    }

    // Enforce ARCHIVED status requires archivedAt
    if (data.status === 'ARCHIVED' && data.archivedAt === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'archivedAt must be set when status is ARCHIVED',
        path: ['archivedAt'],
      });
    }
  });

/**
 * Schema for marking attendance on a booking.
 * Used when the admin marks attendance on the session or event page.
 */
export const markAttendanceRequestSchema = z.object({
  bookingId: z.string(),
  attendance: attendanceStatusSchema,
});

/**
 * Schema for booking fields related to attendance tracking.
 * These fields are set when admin marks attendance.
 */
export const bookingAttendanceSchema = z.object({
  attendance: attendanceStatusSchema.nullable(),
  attendanceMarkedAt: z.date().nullable(),
  attendanceMarkedBy: z.string().nullable(),
});

/**
 * Schema for booking fields related to duration option selection.
 * Set when booking a service with hasDurationOptions=true.
 */
export const bookingDurationOptionSchema = z.object({
  serviceDurationOptionId: z.string().nullable(),
  bookedPrice: z.number().nullable(), // Snapshot of price at booking time
  bookedOptionLabel: z.string().nullable(), // Snapshot of label at booking time
});

// ============================================================================
// Phase 5 Domain 3 Feature 2: Service Admin API Schemas
// ============================================================================

/**
 * Follow-up type enum schema.
 * - MESSAGE: Plain text follow-up message
 * - FORM: Link to a form (requires formId)
 * - GOOGLE_REVIEW: Request for Google review
 */
export const followUpTypeSchema = z.enum(['MESSAGE', 'FORM', 'GOOGLE_REVIEW']);

/**
 * Payment requirement enum schema.
 * - NONE: No payment required
 * - PAY_AT_SESSION: Payment due at session time
 * - DEPOSIT: Partial payment required upfront
 * - FULL_PAYMENT: Full payment required upfront
 */
export const paymentRequirementSchema = z.enum([
  'NONE',
  'PAY_AT_SESSION',
  'DEPOSIT',
  'FULL_PAYMENT',
]);

/**
 * Deposit type enum schema.
 * - FIXED: Fixed amount (e.g., $25)
 * - PERCENTAGE: Percentage of total (e.g., 25%)
 */
export const depositTypeSchema = z.enum(['FIXED', 'PERCENTAGE']);

/**
 * Accepted payment method enum (P5-D17-F3). Mirrors the Prisma `PaymentMethod`
 * enum — no `CASHAPP` (deferred fast-follow). `CARD` is the only online prepay
 * rail authoring may configure (venmo-authoring-removal); the others are
 * in-person/manual contexts. `VENMO` stays in the enum as DORMANT data — existing
 * rows and checkout rails still read it, authoring just can't write it.
 */
export const paymentMethodSchema = z.enum(['CASH', 'VENMO', 'ZELLE', 'CARD', 'OTHER']);

/**
 * Offering delivery context (P5-D17-F3). MVP only ever writes the first value.
 */
export const offeringDeliveryContextSchema = z.enum(['IN_PERSON_OR_REMOTE_SERVICE', 'DIGITAL_GOOD']);

/**
 * Which weekly schedule governs a service's bookable slots
 * (availability-source-choice). Mirrors the Prisma `AvailabilitySource` enum.
 *
 * The source is STORED, never inferred from `ServiceAvailabilityWindow` row
 * presence: a GENERAL service may still hold a full custom week, kept dormant so
 * switching back restores it exactly.
 */
export const availabilitySourceSchema = z.enum(['GENERAL', 'CUSTOM']);

/**
 * The online-prepay rails an offering may advertise (P5-D17-F3 §7.1).
 *
 * CARD-ONLY as of venmo-authoring-removal — Venmo is not an MVP method, so
 * authoring can neither offer nor save it. Reviving Venmo = re-adding `'VENMO'`
 * here plus the two authoring rows; nothing else was deleted.
 */
const ONLINE_PREPAY_METHODS: ReadonlySet<z.infer<typeof paymentMethodSchema>> = new Set(['CARD']);

/**
 * Pure SHAPE validation for `(paymentRequirement, acceptedPaymentMethods)` —
 * the SINGLE source of truth consumed by the create Zod `superRefine` AND the
 * service-layer effective-pair validator (P5-D17-F3 §7.1).
 *
 * SHAPE ONLY. An EMPTY `acceptedPaymentMethods` is valid for ANY requirement. A
 * NON-EMPTY set may only be configured on `FULL_PAYMENT` (the sole online-prepay
 * requirement; `NONE`/`PAY_AT_SESSION` collect in person, `DEPOSIT` is Stripe-only
 * §3.2) and must be a subset of `{CARD}` (reject `VENMO`/`CASH`/`ZELLE`/`OTHER`).
 *
 * WHY EMPTY IS NOT REFUSED HERE — the save-vs-publish line (Gabriel, Decision 35
 * + the Stripe-state ruling; build-time correction 2026-08-05):
 *
 *   SAVE refuses configurations that CONTRADICT THEMSELVES.
 *   PUBLISH refuses configurations that are merely INCOMPLETE.
 *
 * `FULL_PAYMENT` with no chosen method is incomplete, not contradictory, so it is
 * a publish blocker (`PAYMENT_METHOD_MISSING` in `publish-readiness.ts`) and NOT a
 * save error. This is load-bearing for the create canvas: a Paid draft is BORN
 * with Payment deliberately unsatisfied, so refusing the state at save made a paid
 * draft impossible to create at all. `createDraftStore.test.ts`'s "a fresh service
 * buffer builds a create payload the server schema accepts" is the executable
 * statement of that requirement — do not re-add a min-1 rule here without
 * reconciling it.
 *
 * The Tier-1 EDITOR treatment (confirmed Frame 4: amber blocker + disabled Save
 * when the admin turns the last method OFF) is a UI-level rule and is unaffected —
 * it governs an admin actively removing a rail, not a draft that never had one.
 *
 * publish-readiness briefly made empty a save error here and retired the
 * WIZARD-ONLY duplicate of that rule in `wizard.schemas.ts`. The duplicate stays
 * retired; the strictness itself is reverted.
 */
export function checkAcceptedMethodsShape(
  paymentRequirement: z.infer<typeof paymentRequirementSchema> | undefined,
  methods: z.infer<typeof paymentMethodSchema>[] | undefined,
): { ok: boolean; message?: string } {
  const m = methods ?? [];
  if (m.length === 0) return { ok: true };

  if (paymentRequirement !== 'FULL_PAYMENT') {
    return {
      ok: false,
      message: `acceptedPaymentMethods can only be configured when paymentRequirement is FULL_PAYMENT (got ${paymentRequirement ?? 'NONE'}).`,
    };
  }
  const rejected = m.filter((x) => !ONLINE_PREPAY_METHODS.has(x));
  if (rejected.length > 0) {
    return {
      ok: false,
      message: `Card is the only online method — FULL_PAYMENT accepts only CARD (rejected: ${rejected.join(', ')}).`,
    };
  }
  return { ok: true };
}

/**
 * P5-D17-F8 follow-up — "payment not set up" / unbookable predicate.
 *
 * A `FULL_PAYMENT` offering with NO accepted methods has no usable payment rail:
 * a client can't pay, so it's unbookable. This is the single source of truth for
 * three behaviours: the display label ("Payment not set up"), the publish gate
 * (block DRAFT→LIVE until fixed), and the Venmo drift-prune auto-demote
 * (LIVE→DRAFT when a service loses its last method). Deliberately method-only
 * (NOT Stripe-rail-state) — a CARD offering whose Stripe is merely paused is a
 * transient operational issue surfaced on the Payments page, not a config gap.
 */
export function isFullPaymentMissingMethods(
  paymentRequirement: string | null | undefined,
  acceptedPaymentMethods: readonly string[] | null | undefined,
): boolean {
  return paymentRequirement === 'FULL_PAYMENT' && (acceptedPaymentMethods ?? []).length === 0;
}

/**
 * publish-readiness Tier-1 save-validity: a DEPOSIT offering must name how much.
 *
 * Co-present semantics — this only fires when a payload actually declares
 * `paymentRequirement: 'DEPOSIT'`, which is what the Payment editor sends (it
 * saves its whole section group). A partial PATCH that never mentions payment is
 * left alone, exactly like the sibling wizard-prefill refinements. Publish
 * readiness catches anything that slips past by another route.
 *
 * The amount predicate itself is `isDepositAmountValid` from
 * `publish-readiness.ts` so the save blocker and the publish blocker are literally
 * the same rule.
 */
export function checkDepositAmountShape(data: {
  paymentRequirement?: string | null;
  depositType?: string | null;
  depositAmount?: number | null;
  depositPercentage?: number | null;
}): { ok: boolean; message?: string } {
  if (data.paymentRequirement !== 'DEPOSIT') return { ok: true };
  if (isDepositAmountValid(data)) return { ok: true };
  return {
    ok: false,
    message:
      data.depositType === 'PERCENTAGE'
        ? 'Set a deposit percentage between 1 and 100.'
        : 'Set how much of the price is due up front.',
  };
}

/**
 * publish-readiness Tier-1 save-validity: custom availability selected but empty.
 *
 * FIRST-LINE UX, no longer the only enforcement. It used to be editor-only by
 * necessity: both "use the general schedule" and "custom mode with nothing
 * enabled" serialized as the same all-days-disabled grid, so the server could not
 * tell a deliberate clear from an empty custom selection. With
 * `availabilitySource` stored (availability-source-choice), the wire format CAN
 * express the difference, and the service write door rejects the contradiction on
 * the EFFECTIVE source-and-week state. This predicate stays because the editor
 * still has to disable Save before the round trip.
 *
 * Availability is deliberately NOT a publish requirement (ledger Decision 35), so
 * the write door is the only server-side door — which is why both halves of the
 * rule share this one predicate rather than inlining it.
 */
export function isCustomAvailabilitySelectionEmpty(
  days: ReadonlyArray<{ enabled: boolean; slots: ReadonlyArray<unknown> }> | null | undefined,
): boolean {
  return !days?.some((day) => day.enabled && day.slots.length > 0);
}

/**
 * Wire schema for one publish blocker (publish-readiness).
 *
 * Both enums derive from the const arrays the evaluator itself is typed from,
 * so the DTO and the rules cannot drift. Every field is declared because
 * fast-json-stringify silently strips whatever a response schema omits — an
 * under-declared blockers array serializes as `[{}]`.
 */
export const publishBlockerSchema = z.object({
  sectionKey: z.enum(PUBLISH_READINESS_SECTION_KEYS),
  code: z.enum(PUBLISH_BLOCKER_CODES),
  message: z.string(),
});

/**
 * Service reminder schema for notification configuration.
 * Stores when to send reminders before a session.
 */
export const serviceReminderSchema = z.object({
  logicalSlotKey: z.string().regex(/^(startingSoon|headsUp|extra:[A-Za-z0-9:_-]+)$/).optional(),
  id: z.string().optional(),
  serviceId: z.string().optional(),
  minutesBefore: z.number().int().positive().max(MAX_REMINDER_MINUTES_BEFORE),
  message: z.string().nullable().optional(),
  includePrepNotes: z.boolean().default(false),
  // D-04 (2026-08-05): the client-to-admin channel belongs to Chat Relay, so no
  // editor offers a chat button and no door may stamp one on. The default is
  // FAIL-CLOSED for exactly that reason — a payload that omits the field (every
  // canvas-created offering whose admin never opened Notifications) used to
  // persist a chat-on reminder nobody asked for.
  includeChatButton: z.boolean().default(false),
});

/**
 * Service follow-up schema for post-session communication.
 * Stores when and what to send after a session completes.
 */
export const serviceFollowUpSchema = z.object({
  logicalSlotKey: z.string().regex(/^(aftercare|laterCheckIn|extra:[A-Za-z0-9:_-]+)$/).optional(),
  id: z.string().optional(),
  serviceId: z.string().optional(),
  minutesAfter: z.number().int().positive().max(MAX_FOLLOW_UP_MINUTES_AFTER),
  type: followUpTypeSchema,
  message: z.string().nullable().optional(),
  formId: z.string().nullable().optional(),
  // D-04, same fail-closed rule as `serviceReminderSchema` above.
  includeChatButton: z.boolean().default(false),
  // form-linked-to: an off slot is a stored row. `archivedFormName` is the
  // visible reason when the slot's form was archived; an ENABLED FORM slot must
  // name a formId, an off one may not.
  isEnabled: z.boolean().default(true),
  archivedFormName: z.string().nullable().optional(),
});

/**
 * Form type enum values (mirrors Prisma FormType).
 */
const FORM_TYPE_VALUES = ['REGISTRATION', 'INTAKE', 'WAIVER', 'GENERAL'] as const;

/**
 * Form lifecycle status enum values (mirrors Prisma FormStatus).
 */
const FORM_STATUS_VALUES = ['DRAFT', 'ACTIVE', 'ARCHIVED'] as const;

/**
 * Service form assignment schema.
 * Configures which forms are required/optional for a service.
 * Includes form metadata (name, type, status) for display without extra queries.
 */
export const serviceFormAssignmentSchema = z.object({
  formId: z.string(),
  formName: z.string(),
  formType: z.enum(FORM_TYPE_VALUES),
  formStatus: z.enum(FORM_STATUS_VALUES),
  isRequired: z.boolean(),
  allowReuse: z.boolean(),
  reuseWindowDays: z.number().int().positive().max(MAX_REUSE_WINDOW_DAYS).nullable().optional(),
  sortOrder: z.number().int().default(0),
});

/** Ordered gallery image carried by the service-detail DTO. */
export const serviceDetailImageSchema = z.object({
  id: z.string(),
  url: z.string(),
  sortOrder: z.number().int(),
});

/**
 * Service list item schema for paginated listing.
 * Includes stats summary (totalBookings, thisMonthBookings) for card display.
 *
 * Note: Uses z.string().datetime() for API contract (ISO strings over the wire).
 */
export const serviceListItemSchema = serviceSummaryBaseSchema
  .omit({ isActive: true, minimumNoticeMinutes: true, maxConcurrentBookings: true })
  .extend({
    description: z.string().nullable(),
    summary: z.string().nullable().optional(),
    price: z.number().nullable(),
    status: serviceStatusSchema,
    customImage: z.string().nullable(),
    focalPointX: z.number().nullable().optional(),
    focalPointY: z.number().nullable().optional(),
    sortOrder: z.number().int(),
    createdAt: z.string().datetime(),
    totalBookings: z.number().int(),
    thisMonthBookings: z.number().int(),
  });

/**
 * Service detail schema for single service view.
 * Extends list item with full configuration and relations.
 */
export const serviceDetailSchema = serviceListItemSchema.extend({
  summary: z.string().nullable().optional(),
  // Surfaced so the scheduling doors can FREEZE it onto the occurrence
  // alongside the focal coordinates it modifies (snapshot principle,
  // 2026-08-29). Declared like its `focalPointX`/`focalPointY` siblings on the
  // list-item schema rather than `.default(null)`, so it stays consistent with
  // the pair it belongs to.
  focalPointScale: z.number().nullable().optional(),
  images: z.array(serviceDetailImageSchema),
  archivedAt: z.string().datetime().nullable(),
  // Earliest upcoming booking (ISO datetime) or null when none is scheduled,
  // under the D6 canonical "upcoming client booking" predicate (services-
  // bookings-first — same status set as the storefront list's nextBookingAt/
  // upcomingCount and the booked-sessions time=upcoming door, WAITLISTED
  // counts). The detail read path (getById) always populates it.
  //
  // NOTE (services-bookings-first): this field has no consumer on
  // ServiceViewPage.tsx as of this change — the Next-booking tile that read it
  // was deleted (D5, Performance trio recomposed) and the switch-to-draft
  // confirm now derives from the Bookings section's own query total instead
  // (D5). Kept computed/on-the-wire rather than removed — it shipped via
  // services-page-split deviation-1, so deleting it needs Gabriel's say-so
  // (see design.md D6 / open question 4).
  // Optional so pre-existing DTO constructors (fixtures, status-transition paths)
  // that omit it still parse; the detail path never omits it.
  nextBookingAt: z.string().datetime().nullable().optional(),
  hasDurationOptions: z.boolean(),
  durationOptions: z.array(serviceDurationOptionSchema),
  // Location fields
  locationName: z.string().nullable().optional(),
  locationAddress: z.string().nullable().optional(),
  locationCity: z.string().nullable().optional(),
  locationState: z.string().nullable().optional(),
  locationCountry: z.string().nullable().optional(),
  locationLat: z.number().nullable().optional(),
  locationLng: z.number().nullable().optional(),
  // Surfaced 2026-08-29 so the scheduling doors can copy the Google Place
  // link onto occurrences. It exists on the model and always has; the doors
  // dropped it because the DTO never carried it.
  locationPlaceId: z.string().nullable().optional(),
  locationNotes: z.string().nullable().optional(),
  preparationNotes: z.string().nullable().optional(),
  preparationNotesLabel: z.string().nullable().optional(),
  // Virtual session fields
  isVirtual: z.boolean(),
  // service-by-phone: the studio phones the client at the client's own number.
  // `.default()` for fixtures that predate the column.
  isByPhone: z.boolean().default(false),
  virtualPlatform: z.enum(VIRTUAL_PLATFORMS).nullable().optional(),
  // Admin-only DTO: the template's meeting link and passcode, shown in full.
  virtualLink: z.string().nullable().optional(),
  virtualPassword: z.string().nullable().optional(),
  // Booking rules
  minimumNoticeMinutes: z.number().int().nullable(),
  advanceBookingDays: z.number().int().nullable(),
  maxConcurrentBookings: z.number().int(),
  requiresApproval: z.boolean(),
  allowInvites: z.boolean(),
  // Phase 5 Domain 3 F2.6: Recurring event scheduling fields
  isRecurring: z.boolean(),
  rrulePattern: z.string().nullable(),
  horizonDays: z.number().int().nullable(),
  /**
   * DERIVED, never persisted: how far ahead an ACTIVE UNBOUNDED rhythm keeps
   * sessions on the calendar — `max(horizonDays ?? 30, globalAdvance + 1)`,
   * computed per request. Null unless the template has such a rhythm, because
   * "keeps N days scheduled ahead" is only true of one. Distinct from
   * `horizonDays`, which is the template's own stored INPUT: conflating them
   * would let an editor round-trip a computed value back into the column the
   * worker reads, freezing the sweep's window.
   */
  effectiveHorizonDays: z.number().int().nullable().optional(),
  goesLiveDaysBefore: z.number().int().nullable(),
  bookingCutoffMinutes: z.number().int().nullable(),
  // Payment configuration
  paymentRequirement: paymentRequirementSchema,
  refundPolicyMode: z.enum(['INHERIT', 'CUSTOM']).optional(),
  refundsOffered: z.boolean().nullable().optional(),
  refundCutoffMinutesBefore: z.number().int().nullable().optional(),
  depositAmount: z.number().nullable().optional(),
  depositType: depositTypeSchema.nullable().optional(),
  // P5-D17-F3: round-tripped so create/update + the F8 wizard prefill see saved
  // methods, and the update effective-methods computation has `existing.*`.
  // `.default()` keeps the DTO output type required while letting a pre-F3 row /
  // fixture that omits the field still parse (filled to the empty/default value).
  acceptedPaymentMethods: z.array(paymentMethodSchema).default([]),
  // occurrence-boundary (2026-08-27): surfaced on the detail DTO so the
  // scheduling doors can FREEZE it into the occurrence snapshot. The snapshot
  // cannot freeze a term the DTO never carries, and the client event page was
  // reading this live off the template — a client who booked under a flexible
  // policy later saw a strict one. Nullable: a row that never set one has none.
  cancellationPolicy: z.string().nullable().default(null),
  // booking-truth-batch: the CUSTOM minutes travel with the policy so the
  // scheduling doors freeze the pair the client cancel door enforces.
  customCancellationCutoffMinutes: z.number().int().nullable().default(null),
  deliveryContext: offeringDeliveryContextSchema.default('IN_PERSON_OR_REMOTE_SERVICE'),
  // availability-source-choice: the Availability editor opens on THIS, never on
  // whether custom rows exist. `.default()` keeps the field required on the DTO
  // output while letting a fixture that predates the column still parse.
  availabilitySource: availabilitySourceSchema.default('GENERAL'),
  // session-own-link D6: the "On your website" setting, read-only here (it changes through
  // `/api/admin/services/:id/site-link`), and the name's link form, kept by the database.
  // Null link form for an event or template. `.default()` as above, for fixtures that predate the columns.
  siteLinked: z.boolean().default(false),
  linkOnly: z.boolean().default(false),
  webName: z.string().nullable().default(null),
  // Buffer times (Phase 5 Domain 5 — null = inherit global)
  bufferBeforeMinutes: z.number().int().nullable(),
  bufferAfterMinutes: z.number().int().nullable(),
  // Relations
  reminders: z.array(serviceReminderSchema),
  followUps: z.array(serviceFollowUpSchema),
  forms: z.array(serviceFormAssignmentSchema),
  updatedAt: z.string().datetime(),
  // Optional stats summary (when ?include=statsSummary)
  statsSummary: z
    .object({
      totalBookings: z.number().int(),
      thisMonthBookings: z.number().int(),
      attendanceRate: z.number().int().min(0).max(100).nullable(),
    })
    .optional(),
  // Optional wizard prefill data (when ?include=wizard)
  // Contains wizard-specific fields + custom availability in wizard-friendly format
  wizardPrefill: z.lazy(() => wizardPrefillSchema).optional(),
  // Destruction flags (events-page-redesign) — computed authoritatively from
  // dependency counts and matching the service-layer archive/delete guards
  // EXACTLY (DTO-flag/mutation-guard consistency). Present on read paths that
  // resolve dependencies; the event view page renders Archive vs Delete from
  // these, never re-deriving client-side. Events: canDelete iff no bookings and
  // no calendar events; canArchive iff there is history AND no upcoming scheduled
  // sessions (drain-first teardown) AND it's not already archived. Services:
  // canDelete iff no bookings and no calendar events; canArchive iff not already
  // archived.
  canDelete: z.boolean().optional(),
  canArchive: z.boolean().optional(),
  // Publish readiness (publish-readiness) — the structured, ordered blocker list
  // from the ONE shared evaluator. The same result feeds the list card's
  // `publishBlockerCount` and the publish endpoint's 409, so the three surfaces
  // cannot disagree. Each entry names the editor section where the fix happens,
  // and a section contributes at most one blocker. Empty means ready.
  // Optional for literal-compat; the detail endpoint always sets it.
  publishBlockers: z.array(publishBlockerSchema).optional(),
  // service-by-phone D10: true while the service has a booking a switch to or from By phone
  // would strand (an upcoming CONFIRMED or PENDING_APPROVAL one, or a PENDING_PAYMENT one at any
  // time), so the Location editor and the update door agree. Set by the detail read only.
  byPhoneLocked: z.boolean().optional(),
});

/**
 * Duration option schema for create/update requests.
 * Omits id and serviceId (generated server-side).
 */
const createDurationOptionSchema = z.object({
  durationMinutes: z.number().int().positive().max(MAX_SERVICE_DURATION_MINUTES),
  price: z.number().nonnegative(),
  /** Optional display name for this option (e.g., "Coloring Add-On") */
  label: z.string().max(MAX_DURATION_OPTION_LABEL_LENGTH).nullable().optional(),
  /** Optional description of what this option includes */
  description: z.string().max(MAX_DURATION_OPTION_DESCRIPTION_LENGTH).nullable().optional(),
  sortOrder: z.number().int().optional(),
});

// ============================================================================
// Wizard-prefill enums + custom-availability shape (shared by the create/update
// request schemas AND the wizardPrefill response schema below). Defined here so
// createServiceRequestBaseSchema can reference them without a temporal-dead-zone.
// ============================================================================

/**
 * Cancellation policy enum schema (DB format).
 * Prefixed to avoid conflict with wizard input schema.
 */
export const prefillCancellationPolicySchema = z.enum([
  'FLEXIBLE',
  'MODERATE',
  'STRICT',
  'CUSTOM',
]);

/**
 * Balance collection method enum schema (DB format).
 * Prefixed to avoid conflict with calendar types.
 */
export const prefillBalanceCollectionMethodSchema = z.enum([
  'AT_SESSION',
  'BEFORE_SESSION',
]);

/**
 * Time slot in HH:mm format for wizard custom availability.
 */
export const prefillTimeSlotSchema = z.object({
  startTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  endTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
});

/**
 * Day configuration for wizard custom availability.
 * dayOfWeek: 0=Sunday, 6=Saturday
 */
export const prefillCustomAvailabilityDaySchema = z.object({
  dayOfWeek: z.number().int().min(0).max(6),
  enabled: z.boolean(),
  slots: z.array(prefillTimeSlotSchema),
});

/**
 * Custom availability configuration in wizard-friendly format.
 * Used in wizardPrefill response to enable round-trip editing.
 */
export const prefillCustomAvailabilitySchema = z.array(prefillCustomAvailabilityDaySchema);

/**
 * The WRITE flavor of the same grid: slot sanity on top of the shape.
 *
 * Only the create/update doors use it. The read flavor above stays permissive on
 * purpose — a row that predates this rule must still round-trip into the editor
 * where the admin can see and fix it, rather than 500ing the detail response.
 */
export const writeCustomAvailabilitySchema = prefillCustomAvailabilitySchema.superRefine(
  (days, ctx) => {
    for (const issue of findCustomAvailabilityIssues(days)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: issue.message,
        // A null slotIndex means the issue is about the entry itself (a weekday
        // listed twice), so it must not claim a range that may not exist.
        path: issue.slotIndex === null ? [issue.dayIndex] : [issue.dayIndex, 'slots', issue.slotIndex],
      });
    }
  },
);

/**
 * Base create service request schema (before superRefine).
 * Contains all fields for creating a new service.
 */
const createServiceRequestBaseSchema = z.object({
  // A name of only spaces reads as blank everywhere it renders, so `min(1)` is
  // not enough — the old step-1 validator trimmed before testing and so does this.
  name: z
    .string()
    .min(1)
    .max(MAX_NAME_LENGTH)
    .refine((value) => value.trim().length > 0, 'name cannot be only whitespace'),
  description: z.string().max(MAX_DESCRIPTION_LENGTH),
  summary: z.string().trim().max(MAX_SUMMARY_LENGTH).nullable().optional(),
  // Per-type ceiling in the superRefine; this is the absolute one (30 days).
  duration: z.number().int().positive().max(MAX_EVENT_DURATION_MINUTES),
  price: z.number().nonnegative().nullable().optional(),
  isSharedEvent: z.boolean().optional(),
  hasDurationOptions: z.boolean().optional(),
  durationOptions: z.array(createDurationOptionSchema).optional(),
  // Image fields
  customImage: z.string().nullable().optional(),
  galleryImages: z.array(z.string().min(1)).optional(),
  // Focal point is a percentage pair; the framing editor emits 0–100.
  focalPointX: z.number().min(0).max(100).nullable().optional(),
  focalPointY: z.number().min(0).max(100).nullable().optional(),
  // Location fields
  locationName: z.string().nullable().optional(),
  locationAddress: z.string().nullable().optional(),
  locationCity: z.string().nullable().optional(),
  locationState: z.string().nullable().optional(),
  locationCountry: z.string().nullable().optional(),
  locationLat: z.number().nullable().optional(),
  locationLng: z.number().nullable().optional(),
  locationNotes: z.string().nullable().optional(),
  preparationNotes: z.string().nullable().optional(),
  // Write door only — the read DTO above stays uncapped so a longer pre-existing
  // heading round-trips into the editor to be shortened, rather than 500ing.
  preparationNotesLabel: z.string().max(MAX_PREPARATION_LABEL_LENGTH).nullable().optional(),
  // Virtual session fields
  isVirtual: z.boolean().optional(),
  // service-by-phone: services only, never with isVirtual (judged on the effective state).
  isByPhone: z.boolean().optional(),
  // virtual-sessions-finish: the fixed platform list, and the template's meeting
  // link and passcode. Shape only here; the link rule (http/https, no passcode
  // without a link) runs on the EFFECTIVE state in the service layer.
  virtualPlatform: z.enum(VIRTUAL_PLATFORMS).nullable().optional(),
  virtualLink: z.string().max(VIRTUAL_LINK_MAX_LENGTH).nullable().optional(),
  virtualPassword: z.string().max(VIRTUAL_PASSCODE_MAX_LENGTH).nullable().optional(),
  // Booking rules
  minimumNoticeMinutes: z.number().int().nullable().optional(),
  // The booking horizon is a real server-side ceiling (the slot generator will
  // not look past it), and the Booking-rules editor's sub-line has always told
  // admins so — this is the door that makes the claim true.
  advanceBookingDays: z.number().int().min(1).max(MAX_ADVANCE_BOOKING_DAYS).nullable().optional(),
  maxConcurrentBookings: z.number().int().min(1).max(MAX_CONCURRENT_BOOKINGS).optional(),
  // Phase 5 Domain 3 F2.6: Recurring event scheduling fields
  isRecurring: z.boolean().optional(),
  rrulePattern: z.string().nullable().optional(),
  horizonDays: z.number().int().nullable().optional(),
  goesLiveDaysBefore: z.number().int().nullable().optional(),
  bookingCutoffMinutes: z.number().int().max(MAX_BOOKING_CUTOFF_MINUTES).nullable().optional(),
  requiresApproval: z.boolean().optional(),
  allowInvites: z.boolean().optional(),
  sortOrder: z.number().int().optional(),
  // Payment configuration
  paymentRequirement: paymentRequirementSchema.optional(),
  refundPolicyMode: z.enum(['INHERIT', 'CUSTOM']).optional(),
  refundsOffered: z.boolean().nullable().optional(),
  refundCutoffMinutesBefore: z.union([z.literal(1440), z.literal(2880), z.literal(4320), z.literal(10080)]).nullable().optional(),
  depositAmount: z.number().nullable().optional(),
  depositType: depositTypeSchema.nullable().optional(),
  // P5-D17-F3: per-offering online-prepay rails + delivery context.
  acceptedPaymentMethods: z.array(paymentMethodSchema).optional().default([]),
  deliveryContext: offeringDeliveryContextSchema.optional().default('IN_PERSON_OR_REMOTE_SERVICE'),
  // ── Wizard-prefill scalar fields (services-page-split: focused editors PATCH
  // these; previously only the wizard update path wrote them). All are existing
  // Service columns. Cross-field rules enforced in the superRefine below +
  // effective-state service-layer guard, mirroring wizard.mapper.ts exactly. ──
  // Details/About
  hideNameInMessages: z.boolean().optional(),
  // Relative zoom from the framing editor: 1 = fit, 3 = its maximum.
  focalPointScale: z.number().min(1).max(3).nullable().optional(),
  // Booking rules — policies
  cancellationPolicy: prefillCancellationPolicySchema.optional(),
  customCancellationCutoffMinutes: z
    .number()
    .int()
    .positive()
    .max(MAX_CANCELLATION_CUTOFF_MINUTES)
    .nullable()
    .optional(),
  // Booking rules — buffer overrides (null = inherit global). Per-type ceiling in
  // the superRefine; this is the absolute one (the event allowance).
  bufferBeforeMinutes: z.number().int().nonnegative().max(MAX_EVENT_BUFFER_MINUTES).nullable().optional(),
  bufferAfterMinutes: z.number().int().nonnegative().max(MAX_EVENT_BUFFER_MINUTES).nullable().optional(),
  // Booking rules — service invites (session capacity = 1 + maxInvites)
  maxInvites: z.number().int().positive().max(MAX_INVITES).nullable().optional(),
  // Booking rules — custom availability (7-day HH:mm grid → ServiceAvailabilityWindow)
  customAvailability: writeCustomAvailabilitySchema.optional(),
  // Booking rules — which schedule governs the service. Accepted WITHOUT the grid
  // (a General save writes the flag alone and leaves the stored week dormant) and
  // the grid is still accepted without it (the legacy wizard patches grids alone).
  // The contradiction the pair can express — custom source, no hours — needs the
  // stored row to judge, so it lives in the service layer's effective-state guard.
  availabilitySource: availabilitySourceSchema.optional(),
  // Payment — deposit handling
  depositPercentage: z.number().int().min(1).max(100).nullable().optional(),
  balanceCollectionMethod: prefillBalanceCollectionMethodSchema.nullable().optional(),
  balanceDueByMinutesBefore: z.number().int().positive().nullable().optional(),
  balanceReminderMinutesBefore: z.number().int().positive().nullable().optional(),
  // Notifications
  sendRosterBefore: z.boolean().optional(),
  // Event structure (events-page-redesign — the Structure editor PATCHes these;
  // DB-format columns, events only). Cross-field rules (eventDaysCount required
  // iff MULTI_DAY) enforced in the superRefine below + service-layer guard.
  // Inline enum (matches prefillEventStructureTypeSchema, declared later — avoids a
  // temporal-dead-zone reference from this earlier-declared schema).
  eventStructureType: z.enum(['SINGLE_BLOCK', 'MULTI_DAY']).nullable().optional(),
  eventDaysCount: z.number().int().min(2).max(30).nullable().optional(),
  eventMultidayType: z.enum(['CONTINUOUS', 'DAILY']).nullable().optional(),
  // Same ceiling as `duration` above: a per-day block feeds straight into it for
  // a DAILY multi-day run, so an unbounded per-day value was a bypass of the
  // session-length cap rather than a second, looser rule.
  eventDurationPerDayMinutes: z.number().int().positive().max(MAX_EVENT_DURATION_MINUTES).nullable().optional(),
  // Relations (create payloads omit id and serviceId). `reminders` stays REQUIRED
  // but may be empty: F-29 made every pre-session reminder optional, so an admin
  // who turns them all off saves an empty array rather than hitting a guard.
  reminders: z.array(serviceReminderSchema.omit({ id: true, serviceId: true })),
  followUps: z
    .array(serviceFollowUpSchema.omit({ id: true, serviceId: true }))
    .optional(),
  forms: z.array(serviceFormAssignmentSchema.omit({ formName: true, formType: true, formStatus: true })).optional(),
});

// ============================================================================
// Create-flow canvas section completeness schemas (Decision 12)
// ============================================================================

// Photos completeness turns on the COVER, not the grid: wave 4A (ledger Decision 36)
// made `customImage` + its framing the cover of record and left `galleryImages` as
// an optional carousel that never contains the cover.
export const offeringPhotosSectionSchema = z.object({
  customImage: z.string().min(1),
  focalPointX: z.number().min(0).max(100).nullable().optional(),
  focalPointY: z.number().min(0).max(100).nullable().optional(),
  focalPointScale: z.number().min(1).max(3).nullable().optional(),
  galleryImages: z.array(z.string().min(1)).default([]),
});

export const offeringPreparationSectionSchema = z.object({
  preparationNotes: z.string().trim().min(1),
});

export const offeringAvailabilitySectionSchema = z.object({
  customAvailability: prefillCustomAvailabilitySchema.optional(),
});

export const offeringFormsSectionSchema = z.object({
  forms: z.array(serviceFormAssignmentSchema.omit({ formName: true, formType: true, formStatus: true })).default([]),
});

export const offeringNotificationsSectionSchema = z.object({
  // Empty is a legitimate configuration (F-29) — no reminders is a choice.
  reminders: z.array(serviceReminderSchema.omit({ id: true, serviceId: true })),
  followUps: z.array(serviceFollowUpSchema.omit({ id: true, serviceId: true })).default([]),
  sendRosterBefore: z.boolean(),
  hideNameInMessages: z.boolean(),
});

export const eventStructureSectionSchema = z.object({
  eventStructureType: z.enum(['SINGLE_BLOCK', 'MULTI_DAY']),
  eventDaysCount: z.number().int().min(2).max(30).nullable(),
  eventMultidayType: z.enum(['CONTINUOUS', 'DAILY']).nullable(),
  eventDurationPerDayMinutes: z.number().int().positive().nullable(),
});

export const eventRegistrationSectionSchema = z.object({
  maxConcurrentBookings: z.number().int().min(1).max(999),
  bookingCutoffMinutes: z.number().int().positive().nullable(),
  requiresApproval: z.boolean(),
  allowInvites: z.literal(false),
});

export const eventBookingRulesSectionSchema = z.object({
  bufferBeforeMinutes: z.number().int().nonnegative().nullable(),
  bufferAfterMinutes: z.number().int().nonnegative().nullable(),
  cancellationPolicy: prefillCancellationPolicySchema,
  customCancellationCutoffMinutes: z.number().int().positive().nullable(),
});

/**
 * Wizard-prefill cross-field consistency (services-page-split). Enforces the
 * FORBIDDEN combinations that mirror `wizard.mapper.ts`'s clean-state so the
 * PATCH door and the wizard door can never disagree. Shared by create + update.
 *
 * The positive required-iff direction lives in `refineOfferingWriteRules` below,
 * where each rule is gated on the payload NAMING the field that governs it — so
 * a partial PATCH that never mentions payment or cancellation stays lenient
 * while a payload that does mention it must be internally complete.
 */
export function refineWizardPrefillFields(
  data: {
    paymentRequirement?: string;
    depositPercentage?: number | null;
    balanceCollectionMethod?: string | null;
    balanceDueByMinutesBefore?: number | null;
    balanceReminderMinutesBefore?: number | null;
    cancellationPolicy?: string;
    customCancellationCutoffMinutes?: number | null;
    allowInvites?: boolean;
    maxInvites?: number | null;
  },
  ctx: z.RefinementCtx,
): void {
  // Deposit-handling fields are DEPOSIT-only (wizard maps them to null otherwise).
  if (data.paymentRequirement !== undefined && data.paymentRequirement !== 'DEPOSIT') {
    const depositOnly: Array<[string, unknown]> = [
      ['depositPercentage', data.depositPercentage],
      ['balanceCollectionMethod', data.balanceCollectionMethod],
      ['balanceDueByMinutesBefore', data.balanceDueByMinutesBefore],
      ['balanceReminderMinutesBefore', data.balanceReminderMinutesBefore],
    ];
    for (const [field, value] of depositOnly) {
      if (value !== undefined && value !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${field} can only be set when paymentRequirement is DEPOSIT (got ${data.paymentRequirement}).`,
          path: [field],
        });
      }
    }
  }

  // Custom cancellation cutoff is CUSTOM-only.
  if (
    data.cancellationPolicy !== undefined &&
    data.cancellationPolicy !== 'CUSTOM' &&
    data.customCancellationCutoffMinutes !== undefined &&
    data.customCancellationCutoffMinutes !== null
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `customCancellationCutoffMinutes can only be set when cancellationPolicy is CUSTOM (got ${data.cancellationPolicy}).`,
      path: ['customCancellationCutoffMinutes'],
    });
  }

  // maxInvites requires allowInvites (session capacity = 1 + maxInvites).
  if (data.allowInvites === false && data.maxInvites !== undefined && data.maxInvites !== null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'maxInvites can only be set when allowInvites is true.',
      path: ['maxInvites'],
    });
  }
}

/**
 * The rules the retired wizard enforced and the rebuilt create path dropped
 * (dropped-gates sweep 2026-08-06), restored on BOTH write doors at once.
 *
 * Every rule is CO-PRESENT: it fires only when the payload names the field that
 * governs it, which is what lets a focused editor's whole-section PATCH be held
 * to a complete configuration while a one-field PATCH stays untouched. That is
 * the same leniency contract `refineWizardPrefillFields` carries, and it is why
 * these can be shared verbatim between create and update.
 *
 * The offering-type ceilings tighten only on an EXPLICIT `isSharedEvent: false`.
 * A PATCH that omits the discriminator gets the permissive (event) ceiling here —
 * resolving the effective type against the stored row needs a DB read and belongs
 * to the service layer's effective-state guard.
 */
export function refineOfferingWriteRules(
  data: {
    isSharedEvent?: boolean;
    duration?: number;
    durationOptions?: Array<{ durationMinutes: number; price: number }>;
    bufferBeforeMinutes?: number | null;
    bufferAfterMinutes?: number | null;
    price?: number | null;
    paymentRequirement?: string;
    depositAmount?: number | null;
    depositType?: string | null;
    balanceCollectionMethod?: string | null;
    balanceDueByMinutesBefore?: number | null;
    cancellationPolicy?: string;
    customCancellationCutoffMinutes?: number | null;
    reminders?: Array<{ minutesBefore: number }>;
    forms?: Array<{ formId: string; sortOrder?: number }>;
    eventStructureType?: string | null;
    eventDaysCount?: number | null;
    eventMultidayType?: string | null;
    eventDurationPerDayMinutes?: number | null;
  },
  ctx: z.RefinementCtx,
): void {
  const isSession = data.isSharedEvent === false;

  // ── Ceilings a session-type offering holds tighter than an event ──
  if (isSession && data.duration != null && data.duration > MAX_SERVICE_DURATION_MINUTES) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `duration must be at most ${MAX_SERVICE_DURATION_MINUTES} minutes for a session.`,
      path: ['duration'],
    });
  }
  if (isSession) {
    for (const field of ['bufferBeforeMinutes', 'bufferAfterMinutes'] as const) {
      const value = data[field];
      if (value != null && value > MAX_SERVICE_BUFFER_MINUTES) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${field} must be at most ${MAX_SERVICE_BUFFER_MINUTES} minutes for a session.`,
          path: [field],
        });
      }
    }
  }

  // ── Payment ──
  if (data.paymentRequirement !== undefined) {
    // A free offering that advertises a price is a contradiction in the
    // client-visible direction: the hub prints the amount, nothing ever asks for
    // it. The wizard coerced one to the other; the door refuses the pair.
    if (data.paymentRequirement === 'NONE') {
      if (data.price != null && data.price !== 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'price must be 0 when paymentRequirement is NONE.',
          path: ['price'],
        });
      }
      (data.durationOptions ?? []).forEach((option, index) => {
        if (option?.price != null && option.price !== 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'durationOptions prices must be 0 when paymentRequirement is NONE.',
            path: ['durationOptions', index, 'price'],
          });
        }
      });
    }

    // Deposit fields belong to DEPOSIT alone — FULL_PAYMENT keeping a leftover
    // amount is the same stale-state hazard as NONE keeping one.
    if (data.paymentRequirement !== 'DEPOSIT') {
      for (const field of ['depositAmount', 'depositType'] as const) {
        const value = data[field];
        if (value !== undefined && value !== null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${field} must not be set when paymentRequirement is ${data.paymentRequirement}`,
            path: [field],
          });
        }
      }
    } else if (data.balanceCollectionMethod == null) {
      // A deposit with no stated way to collect the rest leaves the balance
      // undefined at runtime; the wizard required the companion on every save.
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Choose how the balance gets collected.',
        path: ['balanceCollectionMethod'],
      });
    }
  }

  if (data.balanceCollectionMethod === 'BEFORE_SESSION' && data.balanceDueByMinutesBefore == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Set how long before the session the balance is due.',
      path: ['balanceDueByMinutesBefore'],
    });
  }

  // ── Cancellation ──
  if (data.cancellationPolicy === 'CUSTOM' && data.customCancellationCutoffMinutes == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Set a custom cancellation cutoff.',
      path: ['customCancellationCutoffMinutes'],
    });
  }

  // ── Duration options ──
  // Only when the offering actually carries choices: an empty array is how a
  // single-duration offering states it has none.
  if (data.durationOptions && data.durationOptions.length > 0) {
    if (
      data.durationOptions.length < MIN_DURATION_OPTIONS ||
      data.durationOptions.length > MAX_DURATION_OPTIONS
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Offer between ${MIN_DURATION_OPTIONS} and ${MAX_DURATION_OPTIONS} durations, or none at all.`,
        path: ['durationOptions'],
      });
    }
  }

  // ── Reminders ──
  // Two reminders at the same offset send the client the same message twice.
  const seenReminderOffsets = new Set<number>();
  (data.reminders ?? []).forEach((reminder, index) => {
    if (seenReminderOffsets.has(reminder.minutesBefore)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Two reminders cannot fire at the same time.',
        path: ['reminders', index, 'minutesBefore'],
      });
    }
    seenReminderOffsets.add(reminder.minutesBefore);
  });

  // ── Forms ──
  // A repeated formId trips the ServiceForm unique index inside the repository;
  // a repeated sortOrder makes the client's form order arbitrary.
  const seenFormIds = new Set<string>();
  const seenFormSortOrders = new Set<number>();
  (data.forms ?? []).forEach((form, index) => {
    if (seenFormIds.has(form.formId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'That form is already attached.',
        path: ['forms', index, 'formId'],
      });
    }
    seenFormIds.add(form.formId);

    if (form.sortOrder !== undefined) {
      if (seenFormSortOrders.has(form.sortOrder)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Two forms cannot share a position.',
          path: ['forms', index, 'sortOrder'],
        });
      }
      seenFormSortOrders.add(form.sortOrder);
    }
  });

  // ── Event structure + full-day block ──
  if (data.eventStructureType === 'MULTI_DAY') {
    if (data.eventDaysCount == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'eventDaysCount is required (2–30) when eventStructureType is MULTI_DAY',
        path: ['eventDaysCount'],
      });
    }
    if (data.eventMultidayType == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'eventMultidayType is required (CONTINUOUS or DAILY) when eventStructureType is MULTI_DAY',
        path: ['eventMultidayType'],
      });
    }
    if (data.eventMultidayType === 'DAILY' && data.eventDurationPerDayMinutes == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'eventDurationPerDayMinutes is required when eventMultidayType is DAILY',
        path: ['eventDurationPerDayMinutes'],
      });
    }
  }
}

/**
 * Create service request schema with payment validation.
 * Enforces that deposit fields are only set with DEPOSIT/FULL_PAYMENT requirement.
 */
function validateRefundPolicyGroup(data: {
  refundPolicyMode?: 'INHERIT' | 'CUSTOM'; refundsOffered?: boolean | null; refundCutoffMinutesBefore?: number | null;
}, ctx: z.RefinementCtx): void {
  if (data.refundPolicyMode === 'CUSTOM' && (typeof data.refundsOffered !== 'boolean' || data.refundCutoffMinutesBefore === undefined)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['refundPolicyMode'], message: 'Custom refund policy requires both policy fields.' });
  }
  if (data.refundPolicyMode === undefined && (data.refundsOffered !== undefined || data.refundCutoffMinutesBefore !== undefined)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['refundPolicyMode'], message: 'Choose INHERIT or CUSTOM with the refund policy fields.' });
  }
}

export const createServiceRequestSchema = createServiceRequestBaseSchema.extend({
  // session-own-link D2: Duplicate's create. The server names the copy `(Copy) <name>`, `(Copy 2) <name>`
  // and so on, the first free in the studio; `name` carries the source's name. Create only, never a PATCH.
  nameFrom: z.literal('copy').optional(),
}).superRefine(
  (data, ctx) => {
    validateRefundPolicyGroup(data, ctx);
    // P5-D17-F3: accepted-methods SHAPE (one source of truth — the same helper
    // the service's effective-pair validator uses). State rules (CARD→Stripe
    // connected, VENMO→handle) need a DB read and live in service-admin.service.ts.
    const shape = checkAcceptedMethodsShape(data.paymentRequirement, data.acceptedPaymentMethods);
    if (!shape.ok) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: shape.message ?? 'Invalid acceptedPaymentMethods for paymentRequirement',
        path: ['acceptedPaymentMethods'],
      });
    }

    // publish-readiness Tier-1: DEPOSIT must name how much (co-present group).
    const deposit = checkDepositAmountShape(data);
    if (!deposit.ok) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: deposit.message ?? 'Invalid deposit amount for paymentRequirement DEPOSIT',
        path: ['depositAmount'],
      });
    }

    // services-page-split: wizard-prefill cross-field consistency.
    refineWizardPrefillFields(data, ctx);

    // dropped-gates sweep: bounds, co-presence, and duplicate rules — the same
    // set the PATCH door runs, so create can never be the looser door.
    refineOfferingWriteRules(data, ctx);

    // Event-only structure fields on something that never said it was an event.
    // CREATE ONLY: a create payload always knows its own type (the essentials
    // sheet seeds `isSharedEvent` either way), whereas a section PATCH sends the
    // structure group WITHOUT the discriminator by design — resolving the type
    // there needs the stored row and belongs to the service layer.
    //
    // This is the rule that stops a nominal session from being handed an event's
    // shape: the multi-day derivation would otherwise compute a several-day
    // `duration` for it, downstream of every ceiling this schema can enforce.
    // Only SET values count — an explicit null is a clean state, not an event claim.
    if (data.isSharedEvent !== true) {
      const eventOnly: Array<[string, boolean]> = [
        ['eventStructureType', data.eventStructureType != null],
        ['eventDaysCount', data.eventDaysCount != null],
        ['eventMultidayType', data.eventMultidayType != null],
        ['eventDurationPerDayMinutes', data.eventDurationPerDayMinutes != null],
      ];
      for (const [field, isSet] of eventOnly) {
        if (isSet) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${field} can only be set when isSharedEvent is true.`,
            path: [field],
          });
        }
      }
    }
  }
);

/**
 * Base update service request schema (partial of create, before superRefine).
 */
const updateServiceRequestBaseSchema = createServiceRequestBaseSchema.partial();

/**
 * Update service request schema with conditional payment validation.
 * Only validates payment fields when both paymentRequirement and deposit fields are provided together.
 */
export const updateServiceRequestSchema = updateServiceRequestBaseSchema.superRefine(
  (data, ctx) => {
    validateRefundPolicyGroup(data, ctx);
    // Only validate if paymentRequirement is explicitly provided in the update
    if (data.paymentRequirement !== undefined) {
      // publish-readiness Tier-1: DEPOSIT must name how much. Co-present only —
      // the Payment editor saves its whole section group, and a PATCH that never
      // mentions payment must stay untouched (same leniency as the sibling
      // wizard-prefill refinements).
      const deposit = checkDepositAmountShape(data);
      if (!deposit.ok) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: deposit.message ?? 'Invalid deposit amount for paymentRequirement DEPOSIT',
          path: ['depositAmount'],
        });
      }
    }

    // services-page-split: wizard-prefill cross-field consistency.
    refineWizardPrefillFields(data, ctx);

    // dropped-gates sweep: the same bounds, co-presence, and duplicate rules the
    // create door runs — including the deposit clean state, which now covers
    // FULL_PAYMENT as well as NONE/PAY_AT_SESSION.
    refineOfferingWriteRules(data, ctx);
  }
);

/**
 * Pagination schema for list responses.
 */
export const paginationSchema = z.object({
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
});

/**
 * Paginated services response schema.
 */
export const paginatedServicesResponseSchema = z.object({
  data: z.array(serviceListItemSchema),
  pagination: paginationSchema,
});

/**
 * Service stats schema for detailed analytics.
 *
 * F7 changes (T-P5D3F7-1):
 * - revenue → serviceRevenue (rename)
 * - Added: onlineCollected, serviceCreatedAt, trendsAverage, popularDay, popularTime, averagePerEvent
 * - attendanceRate changed from float 0.0-1.0 to int 0-100
 */
export const serviceStatsSchema = z.object({
  serviceId: z.string(),
  serviceCreatedAt: z.string().datetime(),
  allTime: z.object({
    totalBookings: z.number().int(),
    uniqueClients: z.number().int(),
    serviceRevenue: z.number().nullable(),
    onlineCollected: z.number().nullable(),
    attendanceRate: z.number().int().min(0).max(100).nullable(),
    noShows: z.number().int(),
    cancellations: z.number().int(),
  }),
  thisMonth: z.object({
    bookings: z.number().int(),
    serviceRevenue: z.number().nullable(),
    onlineCollected: z.number().nullable(),
    attendanceRate: z.number().int().min(0).max(100).nullable(),
    newClients: z.number().int(),
  }),
  trends: z.array(
    z.object({
      month: z.string(), // YYYY-MM format
      bookings: z.number().int(),
      serviceRevenue: z.number().nullable(),
    })
  ),
  trendsAverage: z.number().nullable(),
  popularDay: z.string().nullable(),
  popularTime: z.string().nullable(),
  // Event-specific fields (optional for session-type services)
  eventsHeld: z.number().int().optional(),
  averagePerEvent: z.number().nullable().optional(),
  upcoming: z
    .object({
      nextEvent: z.string().datetime().nullable(),
      registrations: z.number().int(),
      spotsRemaining: z.number().int(),
    })
    .optional(),
});

/**
 * Status transition response schema.
 * Returns the updated service and optional warning message.
 */
export const statusTransitionResponseSchema = z.object({
  success: z.boolean(),
  service: serviceDetailSchema,
  warning: z.string().optional(),
  // session-own-link D2: a restore whose name another service took meanwhile, under its new name.
  renamedTo: z.string().optional(),
});

/**
 * session-own-link D6: the "On your website" setting of a service
 * (`GET` and `PATCH /api/admin/services/:id/site-link`). `url` is the link made from the name,
 * `https://<slug>.<WEB_SITE_BASE_DOMAIN>/services/<webName>`, or null when the studio has no
 * website domain configured or the name has no link form.
 */
export const serviceSiteLinkSchema = z.object({
  siteLinked: z.boolean(),
  linkOnly: z.boolean(),
  webName: z.string().nullable(),
  url: z.string().nullable(),
});

/** Only what the admin changed; a field left out keeps its value. Turning `siteLinked` off turns `linkOnly` off. */
export const serviceSiteLinkChangeSchema = z.object({
  siteLinked: z.boolean().optional(),
  linkOnly: z.boolean().optional(),
});

// ============================================================================
// Phase 5 Domain 3 Feature 4: Wizard Prefill Schemas
// ============================================================================
//
// These schemas define the response format for ?include=wizard on GET /services/:id.
// They use DB enum formats (uppercase) and are prefixed with 'prefill' to avoid
// conflicts with wizard input schemas which use lowercase formats.
// ============================================================================

/**
 * Event structure type enum schema (DB format).
 * Prefixed to avoid conflict with wizard input schema.
 */
export const prefillEventStructureTypeSchema = z.enum(['SINGLE_BLOCK', 'MULTI_DAY']);

/**
 * Event multiday type enum schema (DB format).
 */
export const prefillEventMultidayTypeSchema = z.enum(['CONTINUOUS', 'DAILY']);

/**
 * Event invite type enum schema (DB format - uppercase).
 * Prefixed to avoid conflict with wizard input schema (which uses lowercase).
 */
export const prefillEventInviteTypeSchema = z.enum(['OPEN', 'CLOSED']);

// prefillTimeSlotSchema / prefillCustomAvailabilityDaySchema /
// prefillCustomAvailabilitySchema are defined above createServiceRequestBaseSchema
// (they're referenced by the create/update request schemas too).

/**
 * Wizard prefill data schema.
 * Contains all wizard-specific fields needed to reconstruct wizard state.
 * Only returned when ?include=wizard is requested.
 *
 * Fields are organized by wizard step:
 * - Step 1: hideNameInMessages, focalPointScale
 * - Step 2: cancellationPolicy, buffers, availability windows
 * - Step 3: deposit/balance fields
 * - Step 5: sendRosterBefore
 * - Event-specific fields for event wizards
 */
export const wizardPrefillSchema = z.object({
  // === Step 1: Details Options ===
  hideNameInMessages: z.boolean(),
  focalPointScale: z.number().nullable(),

  // === Step 2: Booking Rules - Policies ===
  cancellationPolicy: prefillCancellationPolicySchema,
  customCancellationCutoffMinutes: z.number().int().nullable(),

  // === Step 2: Booking Rules - Buffer Overrides ===
  bufferBeforeMinutes: z.number().int().nullable(),
  bufferAfterMinutes: z.number().int().nullable(),

  // === Step 2: Booking Rules - Service Invites ===
  maxInvites: z.number().int().nullable(),

  // === Step 2: Custom Availability (wizard-friendly format) ===
  customAvailability: prefillCustomAvailabilitySchema,

  // === Step 3: Payment - Deposit Handling ===
  depositPercentage: z.number().int().nullable(),
  balanceCollectionMethod: prefillBalanceCollectionMethodSchema.nullable(),
  balanceDueByMinutesBefore: z.number().int().nullable(),
  balanceReminderMinutesBefore: z.number().int().nullable(),

  // === Step 5: Notifications ===
  sendRosterBefore: z.boolean(),

  // === Event-Only Fields (null for sessions) ===
  eventStructureType: prefillEventStructureTypeSchema.nullable(),
  eventDaysCount: z.number().int().nullable(),
  eventMultidayType: prefillEventMultidayTypeSchema.nullable(),
  eventDurationPerDayMinutes: z.number().int().nullable(),
  eventInviteType: prefillEventInviteTypeSchema.nullable(),
  invitesPerAttendee: z.number().int().nullable(),
});
